<template>
  <v-dialog v-model="showDialog"
            :persistent="true"
            :no-click-animation="!isEditing"
            :hide-overlay="!isEditing"
            scrollable
            max-width="50%" width="50%"
            content-class="buyer-claim-details-container">
    <v-card v-if="showDialog">
      <v-card-title class="headline">
        <v-layout row>
          <v-flex class="text-xs-left">
            <span class="">{{ labels.pageTitle }}</span>
          </v-flex>
          <v-flex class="text-xs-right">
            <a v-on:click="closeModal"><span class="icon-close icon-enabled"></span></a>
          </v-flex>
        </v-layout>
      </v-card-title>
      <v-card-text class="buyer-claim-details-card-text">
        <div>
          <span>{{ labels.claimIdentifierLabel }}
            <code class="text-lowercase ml-1">{{ currentBuyerClaim.id }}</code></span><br/>

          <span>{{ labels.claimStatusLabel }}
            <code class="text-uppercase ml-1">{{ this.getStatusDescription(currentBuyerClaim.claimStatusDescriptionCode) }}</code></span><br/>

          <span v-if="!!currentBuyerClaim.validationDate">{{ labels.claimValidationDateLabel }}
            <code class="text-lowercase ml-1">{{ $formatDate(currentBuyerClaim.validationDate) }}</code></span><br/>

          <span v-if="!!currentBuyerClaim.cancellationDate">{{ labels.claimCancellationDateLabel }}
            <code class="text-lowercase ml-1">{{ $formatDate(currentBuyerClaim.cancellationDate) }}</code></span>

        </div>
        <v-alert
          :value="isSubmissionBeingProcessed"
          color="info"
          icon="info"
          outline>
          {{ labels.claimSubmissionProcessingMessage.replace("_date_", $formatDate(currentBuyerClaim.submissionTimestamp)) }}
        </v-alert>
        <v-alert
          :value="currentBuyerClaim.isFromExternal"
          color="info"
          icon="info"
          outline>
          {{ labels.readonlyExternalClaimMessage }}
        </v-alert>
        <v-alert
          :value="isFormValidationInError && isEditable"
          color="warning"
          icon="warning"
          outline>
          {{ labels.formValidationErrorMessage }}
        </v-alert>
        <v-divider class="mt-2 mb-2"/>
        <v-form ref="form" v-model="formIsValid" v-show="!isLoadingSomething" onSubmit="return false;" lazy-validation>
          <v-expansion-panel v-model="expandedPanels" expand>
            <v-expansion-panel-content :key="0">
              <template v-slot:header>
                <div>
                  <div class="text-uppercase">{{ labels.buyerAndPolicySectionTitle }}</div>
                </div>
              </template>
              <v-card>
                <v-card-text>
                  <div>
                    <div class="subheading">{{ labels.buyerSelectionHeader }}</div>
                    <div class="pl-2" v-if="isBuyerPolicyReadonly && selectedBuyerInfo">
                      <strong>{{ selectedBuyerInfo.name }}</strong><br/>
                      {{ selectedBuyerInfo.mainIdentifierTypeCode }} {{ selectedBuyerInfo.mainIdentifierValue }}<br/>
                      AZ Id: {{ selectedBuyerInfo.ehId }}
                    </div>
                    <v-autocomplete
                      v-if="!isBuyerPolicyReadonly"
                      v-model="buyerSearchModel"
                      :items="buyerSearchItems"
                      clearable
                      spellcheck="false"
                      item-text="description"
                      item-value="value"
                      :placeholder="labels.buyerSelectionPlaceHolder"
                      :loading="isLoadingBuyers"
                      @change="handleBuyerChange"
                      :rules="[ rules.required ]"
                      return-object
                    ></v-autocomplete>
                  </div>
                  <div class="mt-2">
                    <div class="subheading">{{ labels.policySelectionHeader }}</div>
                    <div class="pl-2" v-if="isBuyerPolicyReadonly && !!selectedPolicyInfo">
                      <strong>
                        {{ selectedPolicyInfo.businessUnitCode }} {{ selectedPolicyInfo.policyId }}
                        {{ !!selectedPolicyInfo.extensionId ? ' - ' + selectedPolicyInfo.extensionId : '' }}
                      </strong><br/>
                      {{ !!selectedPolicyInfo.seller ? selectedPolicyInfo.seller.name : '' }}
                    </div>
                    <v-autocomplete
                      v-if="!isBuyerPolicyReadonly"
                      v-model="policySearchModel"
                      :items="policySearchItems"
                      clearable
                      spellcheck="false"
                      item-text="description"
                      item-value="value"
                      :placeholder="labels.policySelectionPlaceHolder"
                      :loading="isLoadingPolicies"
                      @change="handlePolicyChange"
                      :rules="[ rules.required ]"
                      return-object
                    ></v-autocomplete>
                  </div>
                  <v-alert
                    :value="isClaimableInvoicesEmpty && isClaimCreation"
                    color="info"
                    icon="info"
                    outline>
                    {{ labels.noClaimableInvoicesMessage }}
                  </v-alert>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>

            <v-expansion-panel-content :key="1">
              <template v-slot:header>
                <div class="text-uppercase">{{ labels.claimContextInfoSectionTitle }}</div>
              </template>
              <v-card>
                <v-card-text>
                  <span class="subheading">{{ labels.claimDisputedReasonTitle }}</span>
                  <div class="mt-2">
                    <span>{{ labels.claimDisputedReasonLabel }}</span>
                    <v-autocomplete
                                    v-model="disputedReasonCode"
                                    :items=disputedReasonCodes
                                    class="ma-0 pt-1"
                                    hide-details
                                    clearable
                                    item-text="description"
                                    item-value="code"
                                    :placeholder="labels.claimDisputedReasonPlaceHolder"
                                    :readonly="isContextInfoReadonly"
                                    @change="handleDisputedReasonChange"
                                    return-object />
                  </div>
                </v-card-text>

                <v-card-text>

                  <div v-if="requiredClaimDocuments && requiredClaimDocuments.length > 0" >
                    <span class="subheading">{{ labels.claimRequiredDocumentsUploadTitle }}</span>
                    <DocumentUploaderInline v-for="(item, index) in requiredClaimDocuments"
                                            :key="1000+index"
                                            :accepted-types="'application/pdf,image/jpeg'"
                                            :upload-button-label="labels.documentUploadBtnLabel"
                                            :label="item.fileTypeLabel"
                                            :no-file-attached-label="labels.noFileAttachedLabel"
                                            :document-type="item.fileType"
                                            :document-list="item.documentList"
                                            :show-mandatory-indicator="true"
                                            :show-error-indicator="!formIsValid && !currentBuyerClaim.claimDocuments.some(x => x.documentTypeCode === item.fileType)"
                                            :readonly="isContextInfoReadonly"
                                            :allow-multiple-uploads="true"
                                            @fileUploaded="handleClaimFileUploaded"
                                            @fileRemoved="handleClaimFileRemoved"/>
                  </div>
                  <div class="mt-4" v-if="selectableAdditionalDocuments.length > 0">
                    <span class="subheading">{{ labels.claimOptionalDocumentsUploadTitle }}</span>
                    <DocumentUploaderInline :key="3000"
                                            v-if="!isContextInfoReadonly"
                                            :accepted-types="'application/pdf,image/jpeg'"
                                            :upload-button-label="labels.documentUploadBtnLabel"
                                            :document-type="!!additionalDocumentTypeModel ? additionalDocumentTypeModel.fileType : undefined"
                                            :document-category="'Claim'"
                                            :document-list="[]"
                                            :disabled="!additionalDocumentTypeModel || !additionalDocumentTypeModel.fileType"
                                            @fileUploaded="handleClaimFileUploaded">
                      <template v-slot:label>
                        <v-autocomplete v-model="additionalDocumentTypeModel"
                                        :items="selectableAdditionalDocuments"
                                        class="ma-0 pt-1"
                                        hide-details
                                        clearable
                                        item-text="fileTypeLabel"
                                        item-value="fileType"
                                        :placeholder="labels.additionalDocumentTypePlaceHolder"
                                        return-object>
                        </v-autocomplete>
                      </template>
                    </DocumentUploaderInline>
                  </div>
                  <div class="mt-4" v-if="additionalDocuments.length > 0">
                    <DocumentUploaderInline v-for="(item, index) in additionalDocuments"
                                            :key="2000+index"
                                            :accepted-types="'application/pdf,image/jpeg'"
                                            :upload-button-label="labels.documentUploadBtnLabel"
                                            :label="item.fileTypeLabel"
                                            :no-file-attached-label="labels.noFileAttachedLabel"
                                            :document-type="item.fileType"
                                            :document-list="item.documentList"
                                            :readonly="isContextInfoReadonly"
                                            :allow-multiple-uploads="true"
                                            @fileUploaded="handleClaimFileUploaded"
                                            @fileRemoved="handleClaimFileRemoved"/>
                  </div>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>

            <v-expansion-panel-content :key="2">
              <template v-slot:header>
                <div class="text-uppercase">{{ labels.claimContactInfoSectionTitle }}</div>
              </template>
              <v-card>
                <v-card-text>
                  <div>
                    <div class="subheading">{{ labels.buyerContactTitle }}</div>
                  </div>
                  <div class="elevation-2 pa-2 mt-2">
                    <BuyerClaimContactInfoCard v-model="currentBuyerClaim.buyerContact"
                                               :form-is-valid="formIsValid"
                                               :readonly="isContactInfoReadonly"/>
                  </div>

                  <div class="mt-4">
                    <div class="subheading">{{ labels.policyHolderContactTitle }}</div>
                  </div>
                  <div class="elevation-2 pa-2 mt-2">
                    <BuyerClaimContactInfoCard v-model="currentBuyerClaim.policyHolderContact"
                                               :form-is-valid="formIsValid"
                                               :readonly="isContactInfoReadonly"/>
                  </div>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>

            <v-expansion-panel-content :key="3">
              <template v-slot:header>
                <div class="text-uppercase">{{ labels.claimClaimableInvoicesSectionTitle }}</div>
              </template>
              <v-card>
                <v-card-text>
                  <div class="subheading">{{ labels.claimableInvoiceListLabel }}</div>
                  <v-data-table
                    :headers="claimableInvoicesHeaders"
                    :items="currentBuyerClaim.buyerInvoices"
                    :loading="isLoadingClaimableInvoices"
                    item-key="coverId"
                    class="mt-1">
                    <template v-slot:items="props">
                      <td class="text-xs-left">{{ props.item.invoiceNumber }}</td>
                      <td class="text-xs-left">{{ $formatDate(props.item.invoiceIssueDate) }}</td>
                      <td class="text-xs-left">{{ $formatDate(props.item.invoiceDueDate) }}</td>
                      <td class="text-xs-left text-uppercase">{{ $formatNumber(props.item.invoiceAmount) }}
                        {{ props.item.invoiceCurrencyCode }}
                      </td>
                      <td class="text-xs-right">
                        <DocumentUploaderInline :key="props.item.coverId"
                                                label=""
                                                upload-button-label=""
                                                :no-file-attached-label="labels.noFileAttachedLabel"
                                                :accepted-types="'application/pdf,image/jpeg'"
                                                :document-type="'Invoice'"
                                                :document-list="currentBuyerClaim.invoiceDocuments.filter(x => x.coverId === props.item.coverId)"
                                                :readonly="isClaimableInvoicesReadonly"
                                                :show-error-indicator="!formIsValid && !currentBuyerClaim.invoiceDocuments.some(x => x.coverId === props.item.id)"
                                                @fileUploaded="(v) => handleInvoiceFileUploaded(v, props.item.coverId)"
                                                @fileRemoved="(v) => handleInvoiceFileRemoved(v, props.item.coverId)"/>
                      </td>
                      <td>
                        <v-checkbox v-model="props.item.isDisputed" color="primary" label=""
                          :readonly="isClaimableInvoicesReadonly"
                          :disabled="!!disputedReasonCode && disputedReasonCode.code === 'FRD'"
                          @change="() => handleDisputedInvoiceChange(props.item)" />
                      </td>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-form>
        <div v-if="isLoadingSomething" class="full fill-height justify-center align-center" style="display: flex">
          <v-progress-circular
            :size="100"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-layout row>
          <v-flex xs2 class="pa-1">
            <v-btn v-if="!isClaimCreation" color="grey lighten-3" class="claim-action-btn" @click="handleClaimCancelClick"
                   :disabled="!isCancellable || isLoadingSomething">
              {{ labels.withdrawClaimButtonLabel }}
            </v-btn>
          </v-flex>
          <v-flex xs7>
            <v-spacer/>
          </v-flex>
          <v-flex xs3 class="pa-1">
            <v-btn color="primary" class="claim-action-btn" @click="handleClaimValidateClick" :disabled="!isValidatable"
                   :loading="isSaving">
              {{ labels.validateClaimButtonLabel }}
            </v-btn>
          </v-flex>
          <v-flex xs2 class="pa-1">
            <v-btn color="primary" class="claim-action-btn" @click="handleClaimSaveClick(true)" :disabled="!isEditable"
                   :loading="isSaving">
              {{ labels.saveClaimButtonLabel }}
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card-actions>
    </v-card>
    <ConfirmationDialog ref="confirm" style="z-index: 99999999;"/>
  </v-dialog>
</template>

<script>
import coverApi from '@/services/CoverApi'
import accountApi from '@/services/AccountApi'
import transactorApi from '@/services/TransactorApi'
import DocumentUploaderInline from '@/components/common/DocumentUploaderInline.vue'
import BuyerClaimContactInfoCard from '@/components/cover/buyerclaims/BuyerClaimContactInfoCard.vue'
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue'
import { mapState } from 'vuex'

export default {
  name: 'BuyerClaimDetails',
  props: {
    value: Boolean, // value here is the v-model specified at the parent level
    buyerClaimId: String
  },
  emits: ['buyerClaimUpdated'],
  components: {
    ConfirmationDialog,
    DocumentUploaderInline,
    BuyerClaimContactInfoCard
  },
  data () {
    return {
      labels: {
        pageTitle: this.$t('BuyerClaims.BuyerClaimsDetails.BUYER_CLAIMS_DETAILS_TITLE'),
        buyerAndPolicySectionTitle: this.$t('BuyerClaims.BuyerClaimsDetails.BUYER_AND_POLICY_SECTION_TITLE'),
        buyerSelectionHeader: this.$t('BuyerClaims.BuyerClaimsDetails.BUYER_SELECTION_HEADER'),
        buyerSelectionPlaceHolder: this.$t('BuyerClaims.BuyerClaimsDetails.BUYER_SELECTION_PLACEHOLDER'),
        policySelectionHeader: this.$t('BuyerClaims.BuyerClaimsDetails.POLICY_SELECTION_HEADER'),
        policySelectionPlaceHolder: this.$t('BuyerClaims.BuyerClaimsDetails.POLICY_SELECTION_PLACEHOLDER'),
        claimContextInfoSectionTitle: this.$t('BuyerClaims.BuyerClaimsDetails.CONTEXT_INFO_SECTION_TITLE'),
        claimDisputedReasonTitle: this.$t('BuyerClaims.BuyerClaimsDetails.DISPUTEDREASON_TITLE'),
        claimDisputedReasonLabel: this.$t('BuyerClaims.BuyerClaimsDetails.DISPUTEDREASON_FIELD'),
        claimDisputedReasonPlaceHolder: this.$t('BuyerClaims.BuyerClaimsDetails.DISPUTEDREASON_PLACEHOLDER'),
        claimRequiredDocumentsUploadTitle: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIM_REQUIRED_DOCUMENTS_UPLOAD_TITLE'),
        claimOptionalDocumentsUploadTitle: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIM_OPTIONAL_DOCUMENTS_UPLOAD_TITLE'),
        claimContactInfoSectionTitle: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIM_CONTACT_INFO_SECTION_TITLE'),
        claimClaimableInvoicesSectionTitle: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIM_CLAIMABLE_INVOICES_SECTION_TITLE'),
        saveClaimButtonLabel: this.$t('BuyerClaims.BuyerClaimsDetails.SAVE_CLAIM_BTN'),
        validateClaimButtonLabel: this.$t('BuyerClaims.BuyerClaimsDetails.VALIDATE_CLAIM_BTN'),
        withdrawClaimButtonLabel: this.$t('BuyerClaims.BuyerClaimsDetails.WITHDRAW_CLAIM_BTN'),
        claimIdentifierLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIM_IDENTIFIER_LABEL'),
        claimStatusLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIM_STATUS_LABEL'),
        claimValidationDateLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIM_VALIDATION_DATE_LABEL'),
        claimCancellationDateLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIM_CANCELLATION_DATE_LABEL'),

        yesBtnLabel: this.$t('BuyerClaims.BuyerClaimsDetails.YES_BTN'),
        noBtnLabel: this.$t('BuyerClaims.BuyerClaimsDetails.NO_BTN'),

        documentAccountStatementLabel: this.$t('File.FileType.ACCOUNT_STATEMENT_DESC'),
        documentDeliveryProofLabel: this.$t('File.FileType.PROOF_OF_DELIVERY_DESC'),
        documentPowerOfAttorneyLabel: this.$t('File.FileType.POWER_OF_ATTORNEY_DESC'),
        documentProofOfDebtLabel: this.$t('File.FileType.PROOF_OF_DEBT_DESC'),
        documentUploadBtnLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIM_DOCUMENTS_UPLOAD_BTN'),
        noFileAttachedLabel: this.$t('BuyerClaims.BuyerClaimsDetails.NO_FILE_ATTACHED_LABEL'),
        additionalDocumentTypePlaceHolder: this.$t('File.FileType.ADDITIONAL_DOCUMENTS_DESC'),

        buyerContactTitle: this.$t('BuyerClaims.BuyerClaimsDetails.BUYER_CONTACT_TITLE'),
        policyHolderContactTitle: this.$t('BuyerClaims.BuyerClaimsDetails.POLICY_HOLDER_CONTACT_TITLE'),

        contactSalutationLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CONTACT_SALUTATION_FIELD_NAME'),
        contactFirstNameLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CONTACT_FIRST_NAME_FIELD_NAME'),
        contactLastNameLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CONTACT_LAST_NAME_FIELD_NAME'),
        contactphoneLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CONTACT_PHONE_FIELD_NAME'),
        contactFaxLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CONTACT_FAX_FIELD_NAME'),
        contactBusinessEmailLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CONTACT_BUSINESS_EMAIL_FIELD_NAME'),
        contactAddressLine1Label: this.$t('BuyerClaims.BuyerClaimsDetails.CONTACT_ADDRESS_LINE_1_FIELD_NAME'),
        contactAddressLine2Label: this.$t('BuyerClaims.BuyerClaimsDetails.CONTACT_ADDRESS_LINE_2_FIELD_NAME'),
        contactAddressTownLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CONTACT_ADDRESS_TOWN_FIELD_NAME'),
        contactAddressSubdivisionLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CONTACT_ADDRESS_SUBDIVISION_FIELD_NAME'),
        contactAddressPostalCodeLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CONTACT_ADDRESS_POSTALCODE_FIELD_NAME'),
        contactAddressCountryCodeLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CONTACT_ADDRESS_COUNTRYCODE_FIELD_NAME'),

        claimableInvoiceListLabel: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIMABLE_INVOICE_LIST_LABEL'),
        noClaimableInvoicesMessage: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIMABLE_INVOICES_NO_DATA_MESSAGE'),

        closeConfirmationTitle: this.$t('Common.ConfirmationDialog.DISCARD_CHANGES_CONFIRMATION_TITLE'),
        closeConfirmationMessage: this.$t('Common.ConfirmationDialog.DISCARD_CHANGES_CONFIRMATION_MESSAGE'),
        closeConfirmationNoBtnLabel: this.$t('Common.ConfirmationDialog.DISCARD_CHANGES_CONFIRMATION_NO_BTN'),
        closeConfirmationYesBtnLabel: this.$t('Common.ConfirmationDialog.DISCARD_CHANGES_CONFIRMATION_YES_BTN'),

        cancelConfirmationTitle: this.$t('BuyerClaims.BuyerClaimsDetails.CANCEL_CLAIM_CONFIRMATION_TITLE'),
        cancelConfirmationMessage: this.$t('BuyerClaims.BuyerClaimsDetails.CANCEL_CLAIM_CONFIRMATION_MESSAGE'),
        cancelConfirmationNoBtnLabel: this.$t('Common.ConfirmationDialog.DISCARD_CHANGES_CONFIRMATION_NO_BTN'),
        cancelConfirmationYesBtnLabel: this.$t('Common.ConfirmationDialog.DISCARD_CHANGES_CONFIRMATION_YES_BTN'),

        validateConfirmationTitle: this.$t('BuyerClaims.BuyerClaimsDetails.VALIDATE_CLAIM_CONFIRMATION_TITLE'),
        validateConfirmationMessage: this.$t('BuyerClaims.BuyerClaimsDetails.VALIDATE_CLAIM_CONFIRMATION_MESSAGE'),
        readonlyExternalClaimMessage: this.$t('BuyerClaims.BuyerClaimsDetails.READONLY_EXTERNAL_CLAIM_MESSAGE'),
        claimSubmissionProcessingMessage: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIM_SUBMISSION_PROCESSING_MESSAGE'),
        formValidationErrorMessage: this.$t('BuyerClaims.BuyerClaimsDetails.FORM_VALIDATION_ERROR_MESSAGE')
      },
      currentBuyerClaim: this.getEmptyBuyerClaim(),
      selectedBuyerInfo: {},
      selectedPolicyInfo: { seller: {} },
      isEditing: true,
      isLoadingBuyers: false,
      isLoadingPolicies: false,
      isLoadingClaimableInvoices: false,
      isFormValidationInError: false,
      expandedPanels: [true, false, false, false],
      buyerSearchModel: undefined,
      buyerSearchItems: [],
      policySearchModel: undefined,
      policySearchItems: [],
      additionalDocumentTypeModel: {},
      disputedReasonCode: undefined,
      claimableInvoicesHeaders: [
        {
          text: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIMABLE_INVOICE_NUMBER_COLUMN_HEADER'),
          align: 'left',
          value: 'invoiceNumber'
        },
        {
          text: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIMABLE_INVOICE_ISSUE_DATE_COLUMN_HEADER'),
          value: 'issuedAt'
        },
        { text: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIMABLE_INVOICE_DUE_DATE_COLUMN_HEADER'), value: 'dueAt' },
        { text: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIMABLE_INVOICE_AMOUNT_COLUMN_HEADER'), value: 'amount' },
        {
          text: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIMABLE_INVOICE_UPLOAD_COLUMN_HEADER'),
          value: '',
          sortable: false
        },
        { text: this.$t('BuyerClaims.BuyerClaimsDetails.CLAIMABLE_INVOICE_DISPUTED_COLUMN_HEADER'), value: '' }
      ],
      formIsValid: true,
      isSaving: false,
      rules: {
        required: value => (value === undefined || value === null) ? this.$t('BuyerClaims.BuyerClaimsDetails.REQUIRED_VALIDATION_LABEL') : true
      },
      initialBuyerClaimValue: undefined
    }
  },
  methods: {
    async closeModal () {
      const currentObject = JSON.stringify(this.currentBuyerClaim)
      const isDirty = currentObject !== this.initialBuyerClaimValue
      if (!isDirty || await this.$refs.confirm.open(this.labels.closeConfirmationTitle, this.labels.closeConfirmationMessage, this.labels.closeConfirmationNoBtnLabel, this.labels.closeConfirmationYesBtnLabel)) {
        this.showDialog = false
        this.resetData()
      }
    },
    resetData () {
      this.currentBuyerClaim = this.getEmptyBuyerClaim()
      this.selectedBuyerInfo = undefined
      this.selectedPolicyInfo = undefined
      this.isEditing = true
      this.isLoadingBuyers = false
      this.isLoadingPolicies = false
      this.isLoadingClaimableInvoices = false
      this.expandedPanels = [true, false, false, false]
      this.buyerSearchModel = undefined
      this.buyerSearchItems = []
      this.policySearchModel = undefined
      this.policySearchItems = []
      this.formIsValid = true
      this.isFormValidationInError = false
    },
    getEmptyBuyerClaim () {
      return {
        id: undefined,
        buyerCompanyId: undefined,
        isNonInclusive: false,
        isLegal: false,
        isGuaranteeExisting: true,
        disputedReasonCode: '',
        policy: {
          policyId: undefined,
          businessUnitCode: undefined,
          extensionId: undefined
        },
        buyerContact: {
          contactAddress: {}
        },
        policyHolderContact: {
          contactAddress: {}
        },
        claimDocuments: [],
        buyerInvoices: [],
        invoiceDocuments: [],
        disputedInvoices: [],
        isFromExternal: false,
        claimStatusDescriptionCode: '',
        submissionTimestamp: undefined
      }
    },
    async loadBuyerClaimDetails (buyerClaimId) {
      const buyerClaim = await coverApi.getBuyerClaimById(buyerClaimId)
      const buyerClaimsExtendedInfoData = await coverApi.getBuyerClaimsExtendedInfo([ buyerClaimId ])

      if (!!buyerClaimsExtendedInfoData && buyerClaimsExtendedInfoData.length > 0) {
        delete buyerClaimsExtendedInfoData[0].claimId
        return Object.assign({}, buyerClaim, buyerClaimsExtendedInfoData[0])
      } else {
        return buyerClaim
      }
    },
    async initData () {
      this.isLoadingBuyers = true
      this.isLoadingPolicies = true
      if (!!this.buyerClaimId) {
        this.currentBuyerClaim = await this.loadBuyerClaimDetails(this.buyerClaimId)
        this.loadDisputableInvoices()
      } else {
        this.currentBuyerClaim = this.getEmptyBuyerClaim()
      }

      try {
        if (this.isBuyerPolicyReadonly) {
          this.selectedBuyerInfo = await this.getCompanyInfo(this.currentBuyerClaim.buyerCompanyId)
        } else {
          await this.loadBuyerSearchItems()
        }
      } finally {
        this.isLoadingBuyers = false
      }

      try {
        if (this.isBuyerPolicyReadonly) {
          this.selectedPolicyInfo = await this.getPolicyInfo(this.currentBuyerClaim.policy.businessUnitCode, this.currentBuyerClaim.policy.policyId, this.currentBuyerClaim.policy.extensionId)
        } else {
          await this.loadPolicySearchItems()
        }
      } finally {
        this.isLoadingPolicies = false
      }

      if (!this.currentBuyerClaim.buyerInvoices || this.currentBuyerClaim.buyerInvoices.length === 0) {
        await this.reloadClaimableInvoices()
      }

      this.initialBuyerClaimValue = JSON.stringify(this.currentBuyerClaim)
    },
    async loadBuyerSearchItems () {
      let claimableBuyers = await coverApi.getClaimableBuyers()
      this.buyerSearchItems = claimableBuyers.map((x) => {
        return {
          description: `${x.name} ${x.mainIdentifierValue ? x.mainIdentifierTypeCode : ''} ${x.mainIdentifierValue ? x.mainIdentifierValue : ''} (AZ Id: ${x.ehId})`,
          value: x
        }
      })
        .sort((a, b) => a.value.name.toUpperCase() < b.value.name.toUpperCase() ? -1 : 1)
    },
    async loadPolicySearchItems () {
      let accountInfo = await accountApi.getUserAccountInfo()
      let mappedItems = accountInfo.policies.map((x) => {
        return {
          description: `${x.businessUnitCode} ${x.policyId}${!!x.extensionId ? '-' + x.extensionId : ''} ${!!x.seller ? '-' + x.seller.name : ''}`,
          value: x
        }
      })

      if (mappedItems.length > 1) {
        this.policySearchItems = mappedItems
          .filter(x => !!x.value.seller)
          .sort((a, b) => a.value.seller.name.toUpperCase() < b.value.seller.name.toUpperCase() ? -1 : 1)
      } else {
        this.policySearchItems = mappedItems
        const policyItem = mappedItems[0]
        this.policySearchModel = policyItem
        this.currentBuyerClaim.policy = {
          policyId: policyItem.value.policyId,
          extensionId: policyItem.value.extensionId,
          businessUnitCode: policyItem.value.businessUnitCode
        }
      }
    },
    async getCompanyInfo (companyId) {
      const company = await transactorApi.fetchCompanyById(companyId)
      const principalId = company.companyIdentifiers.find(i => i.isPrincipal === true)
      return {
        ehId: company.id,
        name: company.name,
        mainIdentifierTypeCode: principalId ? principalId.idTypeCode : '',
        mainIdentifierValue: principalId ? principalId.idValue : ''
      }
    },
    async getPolicyInfo (businessUnitCode, policyId, extensionId) {
      const accountInfo = await accountApi.getUserAccountInfo()
      return accountInfo.policies.find(p => p.businessUnitCode === businessUnitCode && p.policyId === policyId && p.extensionId === extensionId)
    },
    async handleBuyerChange (selectedItem) {
      if (!!selectedItem && !!selectedItem.value) {
        this.currentBuyerClaim.buyerCompanyId = selectedItem.value.ehId
        await this.reloadClaimableInvoices()
      } else {
        this.currentBuyerClaim.buyerCompanyId = undefined
      }
    },
    async handlePolicyChange (selectedItem) {
      if (!!selectedItem && !!selectedItem.value) {
        this.currentBuyerClaim.policy = {
          policyId: selectedItem.value.policyId,
          extensionId: selectedItem.value.extensionId,
          businessUnitCode: selectedItem.value.businessUnitCode
        }
        await this.reloadClaimableInvoices()
      } else {
        this.currentBuyerClaim.policy = undefined
      }
    },
    handleClaimFileUploaded (uploadedFile) {
      this.currentBuyerClaim.claimDocuments.push({
        dui: uploadedFile.dui,
        fileName: uploadedFile.fileName,
        documentTypeCode: uploadedFile.documentType
      })
    },
    handleInvoiceFileUploaded (uploadedFile, coverId) {
      this.currentBuyerClaim.invoiceDocuments.push({
        dui: uploadedFile.dui,
        fileName: uploadedFile.fileName,
        coverId: coverId
      })
    },
    handleClaimFileRemoved (dui) {
      const index = this.currentBuyerClaim.claimDocuments.findIndex(x => x.dui === dui)
      if (index >= 0) {
        this.currentBuyerClaim.claimDocuments.splice(index, 1)
      }
    },
    handleInvoiceFileRemoved (dui, coverId) {
      const index = this.currentBuyerClaim.invoiceDocuments.findIndex(x => x.coverId === coverId && x.dui === dui)
      if (index >= 0) {
        this.currentBuyerClaim.invoiceDocuments.splice(index, 1)
      }
    },
    async reloadClaimableInvoices () {
      const buyerId = this.currentBuyerClaim.buyerCompanyId
      const policy = this.currentBuyerClaim.policy
      const businessUnitCode = !!policy ? policy.businessUnitCode : undefined
      const policyId = !!policy ? policy.policyId : undefined
      const extensionId = !!policy ? policy.extensionId : undefined

      if (!!buyerId && !!businessUnitCode && !!policyId) {
        try {
          this.isLoadingClaimableInvoices = true
          const invoices = await coverApi.getClaimableInvoices(buyerId, businessUnitCode, policyId, extensionId)
          this.currentBuyerClaim.buyerInvoices = !!invoices ? invoices : []
        } finally {
          this.isLoadingClaimableInvoices = false
        }
      } else {
        this.currentBuyerClaim.buyerInvoices = []
      }
    },
    async handleClaimSaveClick (changeIsSavingState) {
      if(changeIsSavingState === true) {
        this.isSaving = true
      }
      try {
        const isValid = this.$refs.form.validate()
        this.isFormValidationInError = !isValid
        if (isValid) {
          const response = this.isClaimCreation
            ? await coverApi.createBuyerClaim(this.currentBuyerClaim)
            : await coverApi.updateBuyerClaim(this.currentBuyerClaim)
          this.currentBuyerClaim.id = response.id
          this.currentBuyerClaim.claimStatusCode = response.claimStatusCode
          this.currentBuyerClaim.claimStatusDescriptionCode = response.claimStatusDescriptionCode
          this.selectedBuyerInfo = await this.getCompanyInfo(response.buyerCompanyId)
          this.selectedPolicyInfo = await this.getPolicyInfo(
            response.policy.businessUnitCode,
            response.policy.policyId,
            response.policy.extensionId)
          this.$emit('buyerClaimUpdated')
          this.initialBuyerClaimValue = JSON.stringify(this.currentBuyerClaim)
        }
      } finally {
        if(changeIsSavingState === true) {
          this.isSaving = false
        }
      }
    },
    async handleClaimValidateClick () {
      try {
        const isValid = this.$refs.form.validate()
        this.isFormValidationInError = !isValid
        if (isValid) {
          this.isSaving = true
          await this.handleClaimSaveClick(false)
          if (await this.$refs.confirm.open(this.labels.validateConfirmationTitle, this.labels.validateConfirmationMessage, this.labels.cancelConfirmationNoBtnLabel, this.labels.cancelConfirmationYesBtnLabel)) {
            const isClaimValidated = await coverApi.validateBuyerClaim(this.currentBuyerClaim.id)
            if (isClaimValidated) {
              this.currentBuyerClaim.submissionTimestamp = new Date()
              this.initialBuyerClaimValue = JSON.stringify(this.currentBuyerClaim)
              this.$emit('buyerClaimUpdated')
            }
          }
        }
      } finally {
        this.isSaving = false
      }
    },
    async handleClaimCancelClick () {
      if (this.isCancellable && await this.$refs.confirm.open(this.labels.cancelConfirmationTitle, this.labels.cancelConfirmationMessage, this.labels.cancelConfirmationNoBtnLabel, this.labels.cancelConfirmationYesBtnLabel)) {
        this.isSaving = true
        try {
          const isCancelled = await coverApi.cancelBuyerClaim(this.currentBuyerClaim.id)
          if (isCancelled) {
            this.currentBuyerClaim = await this.loadBuyerClaimDetails(this.currentBuyerClaim.id)
            this.initialBuyerClaimValue = JSON.stringify(this.currentBuyerClaim)
            this.$emit('buyerClaimUpdated')
          }
        } finally {
          this.isSaving = false
        }
      }
    },
    getStatusDescription (statusCode) {
      return statusCode
    },
    loadDisputableInvoices () {
      if (!!this.currentBuyerClaim.disputedReasonCode) {
        this.disputedReasonCode = this.disputedReasonCodes.find(d => d.code === this.currentBuyerClaim.disputedReasonCode)
      }
      if (this.currentBuyerClaim.disputedInvoices === undefined) {
        this.currentBuyerClaim.disputedInvoices = []
      }
      for (let di of this.currentBuyerClaim.disputedInvoices) {
        const invoice = this.currentBuyerClaim.buyerInvoices.find(i => i.invoiceNumber === di)
        if (!!invoice) {
          invoice.isDisputed = true
        }
      }
    },
    handleDisputedReasonChange () {
      this.handleFraudDisputedReason()
      if (!!this.disputedReasonCode) {
        this.currentBuyerClaim.disputedReasonCode = this.disputedReasonCode.code
      } else {
        this.currentBuyerClaim.disputedReasonCode = ''
      }
    },
    handleFraudDisputedReason () {
      const isNewReasonFraud = !!this.disputedReasonCode && this.disputedReasonCode.code === 'FRD'
      if (isNewReasonFraud) {
        this.currentBuyerClaim.buyerInvoices.forEach((i) => { i.isDisputed = true; this.handleDisputedInvoiceChange(i) })
      } else {
        if (this.currentBuyerClaim.disputedReasonCode === 'FRD') {
          this.currentBuyerClaim.buyerInvoices.forEach((i) => { i.isDisputed = false; this.handleDisputedInvoiceChange(i) })
        }
      }
    },
    handleDisputedInvoiceChange (invoice) {
      if (!!invoice && invoice.isDisputed) {
        const index = this.currentBuyerClaim.disputedInvoices.findIndex((number) => number === invoice.invoiceNumber)
        if (index === -1) {
          this.currentBuyerClaim.disputedInvoices.push(invoice.invoiceNumber)
        }
      } else {
        this.currentBuyerClaim.disputedInvoices = this.currentBuyerClaim.disputedInvoices.filter(n => n !== invoice.invoiceNumber)
      }
    }
  },
  computed: {
    ...mapState('userdetails', ['userInfos']),
    showDialog: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    requiredClaimDocuments () {
      const documentTypes = this.userInfos.buyerClaimSettings.documentFileTypes
      const claimDocumentTypes = documentTypes
        .filter(t => t.isMandatory === true)
        .map(t => {
          return {
            fileType: t.code,
            fileTypeLabel: t.description,
            documentList: this.currentBuyerClaim.claimDocuments.filter(d => d.documentTypeCode === t.code)
          }
        })
      return claimDocumentTypes
    },
    additionalDocuments () {
      const documentTypes = this.userInfos.buyerClaimSettings.documentFileTypes
      const result = documentTypes
        .filter(t => t.isMandatory === false)
        .map(t => {
          return {
            fileType: t.code,
            fileTypeLabel: t.description,
            documentList: this.currentBuyerClaim.claimDocuments.filter(d => d.documentTypeCode === t.code)
          }
        })
        .filter(t => t.documentList.length > 0)
      return result
    },
    disputedReasonCodes () {
      return this.userInfos.buyerClaimSettings.disputedReasonCodes
    },
    selectableAdditionalDocuments () {
      const documentTypes = this.userInfos.buyerClaimSettings.documentFileTypes
      const result = documentTypes
        .filter(t => t.isMandatory === false)
        .map(t => {
          return {
            fileType: t.code,
            fileTypeLabel: t.description
          }
        })
      return result
    },
    isClaimableInvoicesEmpty () {
      return !this.isLoadingBuyers && !this.isLoadingPolicies && !this.isLoadingClaimableInvoices &&
        !!this.currentBuyerClaim.buyerCompanyId &&
        !!this.currentBuyerClaim.policy && !!this.currentBuyerClaim.policy.policyId &&
        !!this.currentBuyerClaim.buyerInvoices && this.currentBuyerClaim.buyerInvoices.length === 0
    },
    isBuyerPolicyReadonly () {
      return this.isSaving || (!this.isClaimCreation && !!this.currentBuyerClaim.buyerCompanyId && !!this.currentBuyerClaim.policy.policyId)
    },
    isContextInfoReadonly () {
      return this.isSaving || !this.isEditable
    },
    isContactInfoReadonly () {
      return this.isSaving || !this.isEditable
    },
    isClaimableInvoicesReadonly () {
      return this.isSaving || !this.isEditable
    },
    isClaimCreation () {
      return !this.currentBuyerClaim.id
    },
    isLoadingSomething () {
      return this.isSaving || this.isLoadingBuyers || this.isLoadingPolicies
    },
    isEditable () {
      return this.isDraft && !this.isSaving && !this.isLoadingSomething && !this.currentBuyerClaim.validationDate && !this.currentBuyerClaim.cancellationDate && !this.currentBuyerClaim.isFromExternal && !this.isSubmissionBeingProcessed
    },
    isCancellable () {
      return this.isDraft && !this.currentBuyerClaim.cancellationDate && !this.currentBuyerClaim.isFromExternal && !this.isSubmissionBeingProcessed
    },
    isValidatable () {
      return this.isDraft && !this.isLoadingSomething && !this.currentBuyerClaim.isFromExternal && !this.isSubmissionBeingProcessed
    },
    isSubmissionBeingProcessed () {
      return this.isDraft && !!this.currentBuyerClaim.submissionTimestamp
    },
    isDraft () {
      return (!this.currentBuyerClaim.claimStatusCode && !this.currentBuyerClaim.id) || this.currentBuyerClaim.claimStatusCode === 'DR'
    }
  },
  watch: {
    value: function (newVal, oldVal) {
      if (newVal === true) {
        this.initData()
      }
    }
  }
}
</script>

<style scoped>

.v-dialog__content {
  z-index: 10001 !important;
}

>>> .buyer-claim-details-container {
  min-width: 725px;
  height: 100%;
  min-height: 100%;
  position: absolute;
  right: 0;
  margin: 0;
}

.buyer-claim-details-container .buyer-claim-details-card-text {
  padding-top: 0;
  height: 100%;
}

div.buyer-claim-details-card-text > form > ul > li.v-expansion-panel__container {
  background-color: #dbdbdb;
}

.theme--light.v-table tbody tr, td:not(:first-child) {
  border-bottom: unset;
}

.claim-action-btn {
  width: 100%;
}
</style>
